import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';
import { toast } from 'vue3-toastify';

import coreApi from '~/services/core-api/core-api';
import {
  invalidatePartnerUsersQueries,
  queryKeys,
} from '~/store/queryClient';
import { parseCoreUserGeneralError } from '~/utils/apiFieldErrors';

const MAX_RETRIES = 3;
const NO_RETRY_STATUS = new Set([401, 422]);

export type CreatePartnerUserVariables = Parameters<typeof coreApi.createUser>[0];
export type InactivatePartnerUserVariables = Parameters<typeof coreApi.inactivateUser>[0] & {
  partnerId: number;
};
export type UpdatePartnerUserVariables = {
  data: Parameters<typeof coreApi.updateUser>[1];
  id: Parameters<typeof coreApi.updateUser>[0];
  partnerId: number;
};

/* eslint-disable perfectionist/sort-modules -- helpers antes dos hooks */
function mutationRetry(failureCount: number, error: unknown): boolean {
  if (isAxiosError(error) && error.response && NO_RETRY_STATUS.has(error.response.status)) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}

export function useCreatePartnerUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePartnerUserVariables) => coreApi.createUser(data),
    onSuccess: async (_data, variables) => {
      await invalidatePartnerUsersQueries(queryClient, variables.parceiroId);
      toast.success('Usuário cadastrado com sucesso!');
    },
    retry: mutationRetry,
    retryDelay: (attempt) => attempt * 1000,
  });
}

export function useInactivatePartnerUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ partnerId: _partnerId, ...data }: InactivatePartnerUserVariables) => (
      coreApi.inactivateUser(data)
    ),
    onError: (error: unknown) => {
      toast.error(parseCoreUserGeneralError(error, 'Erro ao inativar usuário'));
    },
    onSuccess: async (_data, variables) => {
      await invalidatePartnerUsersQueries(queryClient, variables.partnerId);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.PARTNER_USERS.DETAILS(variables.userId),
        refetchType: 'active',
      });
      toast.success('Usuário inativado com sucesso!');
    },
    retry: mutationRetry,
    retryDelay: (attempt) => attempt * 1000,
  });
}

export function useUpdatePartnerUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: UpdatePartnerUserVariables) => (
      coreApi.updateUser(variables.id, variables.data)
    ),
    onSuccess: async (_data, variables) => {
      await invalidatePartnerUsersQueries(queryClient, variables.partnerId);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.PARTNER_USERS.DETAILS(variables.id),
        refetchType: 'active',
      });
      toast.success('Usuário atualizado com sucesso!');
    },
    retry: mutationRetry,
    retryDelay: (attempt) => attempt * 1000,
  });
}
/* eslint-enable perfectionist/sort-modules */
